import React from "react";
import { MultiSelect, NumberInput, Rating, Select, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";
import { DateTimePickerInput } from "src/components/forms/DateTimePickerInput";
import { GuidelineMetaData, GuidelineMetaDataQuestion } from "src/state/_types";
import { GenericFieldProps } from "src/utils/forms";

function parseQuestions(value: string | null | undefined): GuidelineMetaDataQuestion[] {
    if (!value) return [];
    try {
        const parsed: GuidelineMetaData = JSON.parse(value);
        return parsed.questions || [];
    } catch {
        return [];
    }
}

function serializeQuestions(questions: GuidelineMetaDataQuestion[]): string {
    const meta: GuidelineMetaData = { questions };
    return JSON.stringify(meta);
}

function normalizeOptions(options?: GuidelineMetaDataQuestion['options']): { label: string; value: string }[] {
    if (!options) return [];
    return options.map(o => typeof o === 'string' ? { label: o, value: o } : o);
}

export const GuidelineMetaDataRenderer: React.FC<GenericFieldProps> = ({ label, onChange, value, disabled, classNames, error }) => {

    const [questions, setQuestions] = React.useState<GuidelineMetaDataQuestion[]>(() => parseQuestions(value));

    const updateAnswer = (index: number, answer: string | null) => {
        const updated = questions.map((q, i) => i === index ? { ...q, answer } : q);
        setQuestions(updated);
        onChange(serializeQuestions(updated));
    };

    const renderInput = (q: GuidelineMetaDataQuestion, index: number) => {
        const common = { required: q.required, disabled };

        switch (q.type) {
            case 'text':
            case 'email':
            case 'phone':
            case 'url':
                return (
                    <TextInput
                        key={index}
                        {...common}
                        label={q.question}
                        type={q.type === 'text' ? undefined : q.type}
                        value={q.answer || ''}
                        onChange={(e) => updateAnswer(index, e.currentTarget.value)}
                    />
                );

            case 'textarea':
                return (
                    <Textarea
                        key={index}
                        {...common}
                        label={q.question}
                        value={q.answer || ''}
                        onChange={(e) => updateAnswer(index, e.currentTarget.value)}
                        autosize
                        minRows={2}
                    />
                );

            case 'number':
            case 'currency':
                return (
                    <NumberInput
                        key={index}
                        {...common}
                        label={q.question}
                        prefix={q.type === 'currency' ? '$' : undefined}
                        decimalScale={q.type === 'currency' ? 2 : undefined}
                        value={q.answer ? Number(q.answer) : ''}
                        onChange={(val) => updateAnswer(index, val !== '' ? String(val) : null)}
                    />
                );

            case 'boolean':
                return (
                    <Select
                        key={index}
                        {...common}
                        label={q.question}
                        data={[
                            { label: 'Yes', value: 'true' },
                            { label: 'No', value: 'false' },
                        ]}
                        value={q.answer || null}
                        onChange={(val) => updateAnswer(index, val)}
                        clearable
                    />
                );

            case 'date': {
                const isSet = typeof q.answer === 'string' && !!q.answer.length;
                let dateValue: Date | null = null;
                if (isSet) {
                    const d = new Date(Date.parse(q.answer!));
                    d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                    dateValue = d;
                }
                return (
                    <DateInput
                        key={index}
                        {...common}
                        label={q.question}
                        value={dateValue}
                        onChange={(val) => {
                            if (typeof val === 'string') {
                                updateAnswer(index, val);
                            } else if (val === null) {
                                updateAnswer(index, null);
                            } else if (typeof val === 'object') {
                                updateAnswer(index, (val as Date).toISOString());
                            }
                        }}
                        clearable
                        dateParser={(dateString) => new Date(Date.parse(dateString))}
                    />
                );
            }

            case 'datetime':
                return (
                    <DateTimePickerInput
                        key={index}
                        label={q.question}
                        id={`metadata-q-${index}`}
                        name={`metadata-q-${index}`}
                        onChange={(val: string) => updateAnswer(index, val || null)}
                        value={q.answer || ''}
                        error={undefined}
                        required={q.required}
                        disabled={!!disabled}
                        description=""
                        classNames={classNames}
                    />
                );

            case 'time':
                return (
                    <TimePicker
                        key={index}
                        {...common}
                        label={q.question}
                        value={q.answer || ''}
                        onChange={(val) => updateAnswer(index, val || null)}
                        withDropdown
                        format="12h"
                    />
                );

            case 'enum':
                return (
                    <Select 
                        key={index}
                        {...common}
                        label={q.question}
                        data={normalizeOptions(q.options)}
                        value={q.answer || null}
                        onChange={(val) => updateAnswer(index, val)}
                        clearable
                    />
                );

            case 'multi-select':
                return (
                    <MultiSelect
                        key={index}
                        {...common}
                        label={q.question}
                        data={normalizeOptions(q.options)}
                        value={q.answer ? q.answer.split(',').map(s => s.trim()) : []}
                        onChange={(val) => updateAnswer(index, val.length ? val.join(', ') : null)}
                        clearable
                    />
                );

            case 'rating':
                return (
                    <div key={index}>
                        <Text fw={500} size="sm">{q.question}{q.required ? ' *' : ''}</Text>
                        <Rating
                            value={q.answer ? Number(q.answer) : 0}
                            onChange={(val) => updateAnswer(index, String(val))}
                            readOnly={disabled}
                        />
                    </div>
                );

            default:
                return (
                    <TextInput
                        key={index}
                        {...common}
                        label={q.question}
                        value={q.answer || ''}
                        onChange={(e) => updateAnswer(index, e.currentTarget.value)}
                    />
                );
        }
    };

    if (!questions.length) {
        return (
            <div className={classNames.root}>
                <Stack gap="sm" className={classNames.wrapper}>
                    <Text fw={500} size="sm" className={classNames.label}>{label}</Text>
                    <Text color="gray" size="sm">No questions to answer</Text>
                </Stack>
            </div>
        );
    }

    return (
        <div className={classNames.root}>
            <Stack gap="sm" className={classNames.wrapper}>
                <Text fw={500} size="sm" className={classNames.label}>{label}</Text>
                {questions.map((q, index) => renderInput(q, index))}
                {error && <Text color="red" size="sm">{error}</Text>}
            </Stack>
        </div>
    );
};
